import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Languages, Printer } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import SignaturePad from "@/components/campaign/components/SignaturePad";
import StampUpload from "@/components/campaign/components/StampUpload";
import { useSignatureAndStamp } from "@/components/campaign/hooks/useSignatureAndStamp";
import { SignedAgreementDocument } from "./SignedAgreementDocument";
import { captureAgreementToPdf } from "./captureAgreementPdf";
import { useToast } from "@/hooks/use-toast";

export interface AgreementStepData {
  applicantName: string;
  companyName: string;
  crNumber: string;
  vatNumber: string;
  email: string;
  personInChargeName: string;
  personInChargeTitle: string;
  nationalIdNumber: string;
  city: string;
}

export interface AgreementResult {
  accepted: boolean;
  signature: string | null;
  stamp: string | null;
  acceptedAt: Date | null;
}

interface AgreementStepProps {
  isAgency: boolean;
  data: AgreementStepData;
  onChange: (result: AgreementResult) => void;
  /** @deprecated Capture is now payload-based; kept for backward compat. */
  onCaptureNodeReady?: (node: HTMLDivElement | null) => void;
}

export const AgreementStep = ({ isAgency, data, onChange, onCaptureNodeReady }: AgreementStepProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [lang, setLang] = useState<'en' | 'ar'>(language === 'ar' ? 'ar' : 'en');
  const [accepted, setAccepted] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState<Date | null>(null);
  const { signature, stamp, handleSignatureSave, handleStampUpload, handleStampRemove } = useSignatureAndStamp();
  const isRTL = language === 'ar';
  const [printing, setPrinting] = useState(false);

  // Backward-compat: clear any previously stored capture-node ref.
  useEffect(() => {
    onCaptureNodeReady?.(null);
    return () => onCaptureNodeReady?.(null);
  }, [onCaptureNodeReady]);

  const sig = signature?.data || null;

  const emit = (next: Partial<AgreementResult>) => {
    const result: AgreementResult = {
      accepted: next.accepted ?? accepted,
      signature: next.signature !== undefined ? next.signature : sig,
      stamp: next.stamp !== undefined ? next.stamp : stamp,
      acceptedAt: next.acceptedAt !== undefined ? next.acceptedAt : acceptedAt,
    };
    onChange(result);
  };

  const onAcceptedChange = (v: boolean) => {
    setAccepted(v);
    const ts = v ? (acceptedAt ?? new Date()) : null;
    if (v && !acceptedAt) setAcceptedAt(ts);
    if (!v) setAcceptedAt(null);
    emit({ accepted: v, acceptedAt: ts });
  };

  const onSignature = (s: string, type: 'draw' | 'upload') => {
    handleSignatureSave(s, type);
    emit({ signature: s || null });
  };

  const onStampChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleStampUpload(e);
    setTimeout(() => emit({}), 100);
  };

  const onStampRemoveLocal = () => {
    handleStampRemove();
    emit({ stamp: null });
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const blob = await captureAgreementToPdf({
        isAgency,
        lang,
        data,
        signature: sig,
        stamp,
        acceptedAt,
        documentDate: acceptedAt ?? new Date(),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isAgency
        ? (lang === 'ar' ? 'عقد-وساطة-إعلانية.pdf' : 'Brokerage-Agreement.pdf')
        : (lang === 'ar' ? 'اشتراك-شريك-إحالة.pdf' : 'Referral-Partner-Subscription.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Print PDF failed:', err);
      toast({
        title: lang === 'ar' ? 'تعذر إنشاء PDF' : 'Could not generate PDF',
        variant: 'destructive',
      });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {isRTL
            ? "يرجى مراجعة الاتفاقية بعناية قبل الإكمال."
            : "Please review the agreement carefully before continuing."}
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={printing}
            className="gap-2"
          >
            <Printer className="h-4 w-4" />
            {lang === 'ar' ? 'طباعة / حفظ PDF' : 'Print / Save PDF'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
            className="gap-2"
          >
            <Languages className="h-4 w-4" />
            {lang === 'en' ? 'العربية' : 'English'}
          </Button>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <ScrollArea className="h-[420px] p-5" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
            <SignedAgreementDocument
              isAgency={isAgency}
              lang={lang}
              data={data}
              signature={sig}
              stamp={stamp}
              acceptedAt={accepted ? acceptedAt : null}
              documentDate={acceptedAt ?? undefined}
            />
          </ScrollArea>
        </CardContent>
      </Card>

      {isAgency && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {isRTL ? "التوقيع المعتمد *" : "Authorized Signature *"}
            </Label>
            <SignaturePad onSave={onSignature} />
          </div>
          <StampUpload
            stamp={stamp}
            onStampUpload={onStampChange}
            onStampRemove={onStampRemoveLocal}
          />
        </div>
      )}

      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
        <Checkbox
          id="agreement-accept"
          checked={accepted}
          onCheckedChange={(v) => onAcceptedChange(!!v)}
          className="mt-0.5"
        />
        <Label htmlFor="agreement-accept" className="text-sm leading-6 cursor-pointer">
          {isAgency
            ? (isRTL
                ? "أقر بأنني قرأت عقد الوساطة الإعلانية أعلاه ووافقت على جميع بنوده، وأن التوقيع والختم المرفقين يصدران بصلاحية تامة عن الطرف الثاني."
                : "I confirm that I have read the brokerage agreement above and agree to all its terms, and that the attached signature and stamp are issued with full authority on behalf of the Second Party.")
            : (isRTL
                ? "أقر بأنني قرأت الإقرار أعلاه وفهمت طبيعة الشراكة وأنه غير ملزم قانونياً."
                : "I confirm that I have read the acknowledgement above and understand the nature of this non-binding partnership.")}
        </Label>
      </div>
    </div>
  );
};

export default AgreementStep;
